import type { MeterReading } from '@/engine/TrackGraph';
import { getServices } from './services';

/**
 * Metering broadcast.
 *
 * Like the playhead, meters update every frame and must not pass through React
 * state. One shared loop reads every track's analyser once per frame and pushes
 * the result to whichever channel strips are mounted; a mixer with forty
 * channels still costs one loop, not forty.
 *
 * Peaks are held and decayed rather than shown raw: a meter that tracks the
 * instantaneous sample peak is unreadable, and a meter with no hold hides
 * exactly the transients an engineer is watching for.
 */

export type MeterListener = (reading: MeterReading, heldPeak: number) => void;

const listeners = new Map<string, Set<MeterListener>>();
const heldPeaks = new Map<string, { value: number; until: number }>();
let frame: number | null = null;

const HOLD_MS = 900;
const DECAY_PER_SECOND = 1.6;

function loop(): void {
  const services = getServices();
  const readings = services.engine.readMeters();
  const now = performance.now();

  for (const [trackId, set] of listeners) {
    const reading = readings.get(trackId);
    if (!reading) continue;

    const peak = Math.max(reading.peakLeft, reading.peakRight);
    const held = heldPeaks.get(trackId);
    let heldValue = held?.value ?? 0;
    if (peak >= heldValue || !held || now > held.until) {
      if (peak >= heldValue) {
        heldValue = peak;
        heldPeaks.set(trackId, { value: peak, until: now + HOLD_MS });
      } else {
        heldValue = Math.max(peak, heldValue - DECAY_PER_SECOND / 60);
        heldPeaks.set(trackId, { value: heldValue, until: now + 16 });
      }
    }

    for (const listener of set) listener(reading, heldValue);
  }

  frame = listeners.size > 0 ? requestAnimationFrame(loop) : null;
}

export function subscribeMeter(trackId: string, listener: MeterListener): () => void {
  let set = listeners.get(trackId);
  if (!set) {
    set = new Set();
    listeners.set(trackId, set);
  }
  set.add(listener);
  if (frame === null) frame = requestAnimationFrame(loop);

  return () => {
    set?.delete(listener);
    if (set && set.size === 0) listeners.delete(trackId);
    if (listeners.size === 0 && frame !== null) {
      cancelAnimationFrame(frame);
      frame = null;
    }
  };
}

/** Maps a linear amplitude to meter height, using a dB scale from -60dB up. */
export function meterScale(amplitude: number): number {
  if (amplitude <= 0.001) return 0;
  const db = 20 * Math.log10(amplitude);
  return Math.min(1, Math.max(0, (db + 60) / 60));
}
